import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, X, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { getPendingInvites, respondToInvite, type PendingInvite } from '../../services/inviteService';
import { useUser } from '../../contextapi/UserContext';

/**
 * PendingInvitePopup
 *
 * Shown on the Dashboard for already-onboarded users who have pending
 * organisation invites. Fetches from the backend by email — no client storage.
 */
export const PendingInvitePopup: React.FC = () => {
  const { user } = useUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [invites, setInvites] = useState<PendingInvite[]>([]);
  const [responding, setResponding] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;
    getPendingInvites()
      .then(setInvites)
      .catch((err) => console.error('Error fetching pending invites:', err));
  }, [user]);

  const visible = invites.filter(
    (inv) => inv.invite_type === 'organisation' && !dismissed.has(inv.id)
  );

  if (visible.length === 0) return null;

  const handleAccept = async (invite: PendingInvite) => {
    setResponding(invite.id);
    try {
      const result = await respondToInvite(invite.invite_code, 'accept');
      if (result.success) {
        toast({ title: 'Joined!', description: `You've joined ${invite.tenant_name ?? 'the organisation'}.` });
        setInvites((prev) => prev.filter((i) => i.id !== invite.id));
        navigate('/dashboard', {
          state: {
            joinedOrganisation: true,
            organisationName: invite.tenant_name ?? 'the organisation',
          },
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to accept invite';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setResponding(null);
    }
  };

  const handleDecline = async (invite: PendingInvite) => {
    setResponding(invite.id);
    try {
      await respondToInvite(invite.invite_code, 'decline');
      toast({ title: 'Declined', description: 'Invite declined.' });
      setInvites((prev) => prev.filter((i) => i.id !== invite.id));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to decline invite';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setResponding(null);
    }
  };

  const handleDismiss = (inviteId: string) => {
    setDismissed((prev) => new Set([...prev, inviteId]));
  };

  return (
    <div className="space-y-3 mb-6">
      {visible.map((invite) => (
        <Card key={invite.id} className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-2 flex-shrink-0">
                  <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-300" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    Organisation Invite
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    <span className="font-medium">{invite.sender_name}</span> invited you to join{' '}
                    <span className="font-medium">{invite.tenant_name ?? 'an organisation'}</span>
                    {invite.role && invite.role !== 'org_admin' && (
                      <> as <span className="font-medium capitalize">{invite.role.replace(/_/g, ' ')}</span></>
                    )}
                    .
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleDismiss(invite.id)}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex gap-2 mt-3 ml-11">
              <Button
                size="sm"
                onClick={() => handleAccept(invite)}
                disabled={responding === invite.id}
                className="gap-1.5"
              >
                {responding === invite.id ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CheckCircle className="h-3.5 w-3.5" />
                )}
                Accept
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDecline(invite)}
                disabled={responding === invite.id}
                className="gap-1.5"
              >
                <XCircle className="h-3.5 w-3.5" />
                Decline
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
